import type { CategoryViewModel } from "@/lib/domains";
import { memo } from "react";

interface CategoryChipProps {
    category: CategoryViewModel;
    selected?: boolean;
    onClick?: () => void;
    size?: 'sm' | 'md' | 'lg';
    showLabel?: boolean;
}

/**
 * CategoryChip component - displays a category as a selectable chip.
 * Memoized because it's rendered multiple times in category selectors.
 */
function CategoryChipComponent({
    category,
    selected = false,
    onClick,
    size = 'md',
    showLabel = true
}: CategoryChipProps) {
    const sizeClasses = {
        sm: 'w-16 h-16 text-xs',
        md: 'w-20 h-20 text-sm',
        lg: 'w-24 h-24 text-base',
    };

    const iconSizes = {
        sm: 'text-2xl',
        md: 'text-3xl',
        lg: 'text-4xl',
    };

    return (
        <button
            type="button"
            onClick={onClick}
            role="button"
            aria-label={`Select ${category.displayName} category`}
            aria-pressed={selected}
            className={`
                flex flex-col items-center justify-center gap-1 p-2 rounded-xl
                transition-all duration-200 border-2
                ${selected
                    ? 'border-blue-500 bg-blue-50 shadow-md scale-105'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
                ${sizeClasses[size]}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                active:scale-95
            `}
            style={{
                backgroundColor: selected ? `${category.color}20` : undefined,
            }}
        >
            <span
                className={`${iconSizes[size]}`}
                aria-hidden="true"
            >
                {category.icon}
            </span>
            {showLabel && (
                <span className="text-xs font-medium text-gray-700 text-center line-clamp-2 leading-tight">
                    {category.displayName}
                </span>
            )}
        </button>
    );
}

/**
 * Memoized version prevents re-renders when category selection state hasn't changed.
 * Important for performance when rendering many category options.
 */
const CategoryChip = memo(CategoryChipComponent);
export default CategoryChip;
